import {
  Contact,
  Entity,
  InvoiceStatus,
  OtherRecipientInput,
  PaymentFrequency,
  PaymentMethod,
  TaskSettlementStatus,
  TaxType,
} from '@admiin-com/ds-graphql';
import invoiceString from './invoice';
import * as Handlebars from 'handlebars';
import { DateTime } from 'luxon';
import { getFromS3Storage } from '@admiin-com/ds-amplify';
import { DATE_FORMATS, userDateFromBackendDate } from '@admiin-com/ds-common';
import { registerHandlebarsHelpers } from '../../helpers/handlebars';

/**
 * Minimum threshold for considering an amount as non-zero (in dollars).
 * Amounts below this value (e.g., due to floating point precision) are treated as NIL.
 */
const MINIMUM_AMOUNT_THRESHOLD = 0.005;

const compileTemplate = async (templateSource: string, data: any) => {
  registerHandlebarsHelpers();
  const template = Handlebars.compile(templateSource);
  return template(data);
};

type InvoiceData = {
  from: any; //TODO: types Entity or AutocompleteResult
  to: any; //TODO: types Contact or Entity
  type: string;
  paymentFrequency: PaymentFrequency;
  amount: string;
  dueAt: string;
  reference: string;
  bpayReferenceNumber?: string;
  notes: string;
  lodgementAt: string;
  annotations: any;
  gstInclusive: boolean;
  numberOfPayments: number;
  settlementStatus?: TaskSettlementStatus | null;
  paymentAt: string;
  firstPaymentAt: string;
  lineItems: {
    serviceId?: string | null;
    description?: string | null;
    quantity?: number | null;
    unitAmount?: number | null;
    taxType?: string | null;
    lineAmount?: number | null;
    newService?: boolean | null;
  }[];
  invoiceStatus: InvoiceStatus;
  sendSMS: boolean;
  otherRecipients: OtherRecipientInput[];
  sendCopyToContact: boolean;
  brandColor?: string;
  quoteRequiresSignature?: boolean;
};
const hexWithOpacity = (hex: string, opacity: number) => {
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alpha}`;
};

export function formatNumberWithCommas(
  value?: number,
  withDecimals = false
): string {
  if (!value) return '';

  return withDecimals
    ? value?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : value?.toLocaleString('en-US');
}

export function formatCurrency(value: number | null): string {
  return value !== null
    ? `$${value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : '';
}

/**
 * Formats a settlement amount as currency with commas, wrapping in brackets if refundable.
 * @param amountInCents - The amount in cents
 * @param settlementStatus - The settlement status (e.g., 'REFUNDABLE', 'PAYABLE', 'NIL')
 * @returns Formatted currency string, e.g., '$1,500.00' or '($1,500.00)' for refundable
 */
export function formatSettlementAmount(
  amountInCents: number | null | undefined,
  settlementStatus?: string | null
): string {
  if (amountInCents === null || amountInCents === undefined) {
    return '';
  }

  const amountInDollars = amountInCents / 100;
  return formatSettlementAmountDollars(amountInDollars, settlementStatus);
}

/**
 * Formats a settlement amount (in dollars) as currency with commas, wrapping in brackets if refundable.
 * @param amountInDollars - The amount in dollars (not cents)
 * @param settlementStatus - The settlement status (e.g., 'REFUNDABLE', 'PAYABLE', 'NIL')
 * @returns Formatted currency string, e.g., '$1,500.00' or '($1,500.00)' for refundable
 */
export function formatSettlementAmountDollars(
  amountInDollars: number | null | undefined,
  settlementStatus?: string | null
): string {
  if (amountInDollars === null || amountInDollars === undefined) {
    return '';
  }

  const normalizedStatus = (settlementStatus || '').toString().toUpperCase();

  if (
    normalizedStatus === 'NIL' ||
    Math.abs(amountInDollars) < MINIMUM_AMOUNT_THRESHOLD
  ) {
    return 'NIL';
  }

  const formattedAmount = `$${Math.abs(amountInDollars).toLocaleString(
    'en-US',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;

  if (normalizedStatus === 'REFUNDABLE') {
    return `(${formattedAmount})`;
  }

  return formattedAmount;
}

export const getAmountFromLineItems = (
  rows: {
    serviceId?: string | null;
    description?: string | null;
    quantity?: number | null;
    unitAmount?: number | null;
    taxType?: string | null;
    lineAmount?: number | null;
    newService?: boolean | null;
  }[]
) => {
  const subTotal = rows.reduce(
    (acc, row) => acc + (row?.unitAmount ?? 0) * (row.quantity ?? 0),
    0
  );

  const totalGST = rows.reduce(
    (acc, row) =>
      acc +
      (row?.unitAmount ?? 0) *
        (row.quantity ?? 0) *
        (row.taxType === TaxType.GST ? 0.1 : 0),
    0
  );
  const total = subTotal + totalGST;
  return {
    subTotal,
    totalGST,
    total,
  };
};

// Helper function to get period description text
const getFrequencyPeriodDescription = (
  frequency: string,
  dueDate: string
): string => {
  if (!dueDate) return '';

  try {
    const start = DateTime.fromISO(dueDate);
    if (!start.isValid) return '';

    switch (frequency) {
      case 'WEEKLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ weeks: 1 })
          .toFormat('dd MMM yyyy')}`;
      case 'FORTNIGHTLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ weeks: 2 })
          .toFormat('dd MMM yyyy')}`;
      case 'MONTHLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ months: 1 })
          .toFormat('dd MMM yyyy')}`;
      case 'QUARTERLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ months: 3 })
          .toFormat('dd MMM yyyy')}`;
      case 'ANNUALLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ years: 1 })
          .toFormat('dd MMM yyyy')}`;
      default:
        return '';
    }
  } catch (error) {
    return '';
  }
};

const generateInvoiceContent = async (
  task: InvoiceData,
  customHtml?: string
) => {
  const { subTotal, totalGST, total } = getAmountFromLineItems(task.lineItems);

  const contact: Contact | Entity = task.to;
  const entity = task.from;

  const getReceivingAccount = (entity: any) => {
    if (!entity || !entity.paymentMethods) {
      return null;
    }
    return entity.paymentMethods.items.find(
      (method: PaymentMethod) => method?.id === entity.disbursementMethodId
    );
  };

  let logoUrl = null;
  try {
    if (entity?.logo?.src) {
      logoUrl = entity?.logo?.src;
    }
    if (entity?.logo?.key) {
      logoUrl = await getFromS3Storage({
        key: entity?.logo?.key,
        identityId: entity?.logo?.identityId,
        level: entity?.logo?.level,
      });
    }
  } catch (error) {
    console.error('Error getting logo URL:', error);
  }

  console.log('entity', entity);
  const receivingAccount = getReceivingAccount(entity);
  console.log('receivingAccount', receivingAccount);

  const color = task.brandColor ?? entity?.brandColour ?? '#8C52FF';
  const brandColorLight = hexWithOpacity(color, 0.1);
  // Calculate frequency period for recurring payments
  const frequencyPeriod =
    task.paymentFrequency !== PaymentFrequency.ONCE
      ? getFrequencyPeriodDescription(task.paymentFrequency, task.dueAt)
      : '';

  // extreact invoice data
  const today = DateTime.now().toFormat(DATE_FORMATS.USER_DATE);
  const invoiceData = {
    task: {
      reference: task.reference ?? '',
      dueAt: task.dueAt
        ? userDateFromBackendDate(task.dueAt)
        : DateTime.now().toFormat(DATE_FORMATS.DUE_DATE),
      notes: task.notes || '',
      invoiceStatus: task.invoiceStatus,
      dueTitle: task.invoiceStatus === 'QUOTE' ? 'Valid Until' : 'Due On',
      issuedAt: today,
      documentTitle:
        task.invoiceStatus === InvoiceStatus.INVOICE ? 'TAX INVOICE' : 'QUOTE',
      signatureRequired:
        task.invoiceStatus === InvoiceStatus.QUOTE &&
        Boolean(task.quoteRequiresSignature),
      frequencyPeriod: frequencyPeriod,
      amount: task.amount,
      settlementStatus: task.settlementStatus,
    },
    to: {
      name: contact?.name ?? '',
      firstName:
        (contact as Contact)?.firstName ??
        (contact as Entity)?.contact?.firstName ??
        '',
      lastName:
        (contact as Contact)?.lastName ??
        (contact as Entity)?.contact?.lastName ??
        '',
      taxNumber: contact?.taxNumber ?? '',
      address: contact?.address || {},
      email:
        (contact as Contact)?.email ??
        (contact as Entity)?.contact?.email ??
        '',
      phone:
        (contact as Contact)?.mobile ??
        (contact as Contact)?.phone ??
        (contact as Entity)?.contact?.phone ??
        '',
    },
    from: {
      name: entity?.name ?? '',
      taxNumber: entity?.taxNumber ?? '',
      address: entity?.address || {},
      contact: {
        firstName: entity?.contact?.firstName ?? '',
        lastName: entity?.contact?.lastName ?? '',
        email: entity?.contact?.email ?? '',
        phone: entity?.contact?.phone ?? '',
      },
      website: entity?.website ?? '',
      brandColor: color,
      brandColorLight: brandColorLight,
      logoUrl: logoUrl,
    },
    lineItem: task.lineItems?.map((item) => ({
      description: item.description,
      //unitPrice: item.unitAmount?.toString(),
      unitPrice: formatNumberWithCommas(
        item.unitAmount ? item.unitAmount : 0,
        true
      ),
      qty: item.quantity?.toString(),
      gst: item.taxType?.toString(),
      amount: formatNumberWithCommas(
        item.lineAmount ? item.lineAmount : 0,
        true
      ),
    })),
    receivingAccount: {
      accountName: receivingAccount?.accountName ?? entity?.name ?? '',
      routingNumber: receivingAccount?.routingNumber,
      number: receivingAccount?.accountNumber,
      // accountName: contact?.bank?.accountName.toString() ?? '',
      // routingNumber: contact?.bank?.routingNumber.toString() ?? '',
      // number: contact?.bank?.accountNumber.toString() ?? '',
    },
    pageNumber: 1,
    pageCount: 1,
    subTotal: formatCurrency(subTotal),
    totalGST: formatCurrency(totalGST),
    total: formatCurrency(total),
    partner: {
      firstName: entity.contact?.firstName ?? '',
      lastName: entity.contact?.lastName ?? '',
      email: entity.contact?.email ?? '',
      phone: entity.contact?.phone ?? '',
    },
  };

  const compiledTemplate = await compileTemplate(
    customHtml ?? invoiceString,
    invoiceData
  );
  return compiledTemplate;
};
export {
  invoiceString,
  generateInvoiceContent,
  compileTemplate,
  getFrequencyPeriodDescription,
};
